import { useMemo } from 'react'

import { isEmpty } from 'lodash'

import { interWbHttp } from '@interco/inter-webview-bridge'
import { useQuery } from '@tanstack/react-query'

import {
  TAuthId,
  TAuthorizationsResponse,
  TPostAuthorization,
  TPostAuthorizationResponse,
} from './types'

function useGetAuthorization({ id }: { id?: string }) {
  const { data, isLoading, isError, error } = useQuery<TAuthorizationsResponse>({
    queryKey: ['GET_AUTHORIZATION'],
    queryFn: () => {
      if (!id) throw new Error('unidentified')
      return getAuthorization({ id })
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  })

  const { client, consentRequested, redirectUri } = useMemo(() => {
    if (isLoading || isError || isEmpty(data))
      return {
        client: {},
        consentRequested: {},
        redirectUri: '',
      } as TAuthorizationsResponse
    return data
  }, [data, isLoading, isError])

  return { client, consentRequested, redirectUri, isLoading, isError, error }
}

export const getAuthorization = async ({ id }: TAuthId): Promise<TAuthorizationsResponse> => {
  const endpoint = `/interid/v1/authorizations/${id}`

  const { response } = await interWbHttp.get(endpoint)

  const data = JSON.parse(response)

  return data
}

export const postAuthorization = async ({
  id,
  authorizedClaims,
  authorizedScope,
}: TPostAuthorization): Promise<TPostAuthorizationResponse> => {
  const endpoint = `/interid/v1/authorize-session`

  const { response } = await interWbHttp.post(endpoint, {
    authorizationId: id,
    authorizedClaims,
    authorizedScope,
  })

  const data = JSON.parse(response)

  return data
}

export { useGetAuthorization }
